/**
 * Damage scenario CSV / JSON import + export helpers.
 *
 * Mirror of step2-assets/assetCsv.ts. Header row matches the table's
 * read-mode column labels (ID / Assets / Compromised / Scenario /
 * S / F / O / P / Impact). Array cells (`asset_ids`,
 * `compromised_properties`) are joined with `; ` inside quoted cells
 * on export and split back on `;` / newline on import.
 */
import { SCENARIO_COLUMNS_READ, type ScenarioRecord } from './scenarioFieldSchema';

const EXPORT_COLUMNS = SCENARIO_COLUMNS_READ;

const escapeCsv = (raw: unknown): string => {
  if (raw == null) return '';
  if (Array.isArray(raw)) return `"${raw.join('; ').replace(/"/g, '""')}"`;
  const s = String(raw);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

/** Trigger a CSV file download with `damage_scenarios_YYYY-MM-DD.csv` filename. */
export function exportScenariosToCsv(scenarios: ScenarioRecord[]): void {
  const header = EXPORT_COLUMNS.map((c) => c.label).join(',');
  const rows = scenarios.map((s) =>
    EXPORT_COLUMNS.map((c) => escapeCsv((s as Record<string, unknown>)[c.key])).join(','),
  );
  const csv = [header, ...rows].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `damage_scenarios_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  window.URL.revokeObjectURL(url);
}

/** Minimal CSV parser — handles quoted fields with embedded commas
 *  and newlines. Identical shape to assetCsv's `parseCsvText`. */
function parseCsvText(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += ch;
    }
  }
  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.length > 0));
}

/** The single-letter SFOP headers in the export need an explicit
 *  reverse-map: `'S'` → `'safety'`, not `'s'` after the lowercase /
 *  snake transform. The other columns happen to match — `'ID'` →
 *  `'id'` which we override to `damage_id`. */
const HEADER_OVERRIDES: Record<string, string> = {
  id: 'damage_id',
  assets: 'asset_ids',
  compromised: 'compromised_properties',
  scenario: 'damage_scenario',
  s: 'safety',
  f: 'financial',
  o: 'operational',
  p: 'privacy',
  impact: 'final_impact_rating',
};

function headerToKey(header: string): string {
  const col = EXPORT_COLUMNS.find(
    (c) => c.label.toLowerCase() === header.trim().toLowerCase(),
  );
  if (col) return col.key;
  const slug = header.trim().toLowerCase().replace(/\s+/g, '_');
  return HEADER_OVERRIDES[slug] ?? slug;
}

const LIST_KEYS = new Set(['asset_ids', 'compromised_properties']);

/** Parse a CSV file into scenario records. List columns split on
 *  `;` or newline; trim whitespace; drop empty fragments. */
export function parseScenarioCsv(text: string): ScenarioRecord[] {
  const rows = parseCsvText(text);
  if (rows.length === 0) return [];
  const headers = rows[0].map(headerToKey);
  return rows.slice(1).map((row) => {
    const out: Record<string, unknown> = {};
    headers.forEach((key, i) => {
      const raw = row[i] ?? '';
      out[key] = LIST_KEYS.has(key)
        ? raw
            .split(/[;\n]/)
            .map((s) => s.trim())
            .filter(Boolean)
        : raw;
    });
    return out as ScenarioRecord;
  });
}

/** Parse a JSON file. Accepts `[…]`, `{ damage_scenarios: [...] }`,
 *  or `{ scenarios: [...] }`. */
export function parseScenarioJson(text: string): ScenarioRecord[] {
  const parsed = JSON.parse(text);
  if (Array.isArray(parsed)) return parsed as ScenarioRecord[];
  if (parsed && Array.isArray(parsed.damage_scenarios)) {
    return parsed.damage_scenarios as ScenarioRecord[];
  }
  if (parsed && Array.isArray(parsed.scenarios)) {
    return parsed.scenarios as ScenarioRecord[];
  }
  throw new Error(
    'JSON must be an array of damage scenarios or `{ damage_scenarios: [...] }`',
  );
}

/** Dispatcher: CSV vs JSON by file extension; JSON-first fallback otherwise. */
export async function parseScenarioFile(file: File): Promise<ScenarioRecord[]> {
  const text = await file.text();
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (ext === 'json') return parseScenarioJson(text);
  if (ext === 'csv') return parseScenarioCsv(text);
  try {
    return parseScenarioJson(text);
  } catch {
    return parseScenarioCsv(text);
  }
}
